// App.tsx
import React, { useState, useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Login from "./Pages/Login";
import AdminPanel from "./Pages/AdminPanel";
import Dashboard from "./Pages/Dashboard";
import CheckLog from "./Pages/CheckLog";
import AuditLog from "./Pages/AuditLog";
import SuperAdminDashboard from "./Pages/SuperAdminDashboard";
import { isSuperAdminSubdomain, isAdminLike } from "./services/utils/utiltyFunctions";

import NavBar from "./components/Navbar/Navbar";
import { useAuth } from "./components/Contexts/AuthContext";
import { OnlineUsersProvider } from "./components/Contexts/OnlineUsersContext";
import { SystemsProvider } from "./components/Contexts/SystemsContext"; // Import SystemsProvider
import { BillingProvider } from "./components/Contexts/BillingContext";
import BillingBanner from "./components/BillingBanner";
import { isSandbox } from "./sandbox/session";
import SandboxGate from "./sandbox/SandboxGate";
import SandboxBar from "./sandbox/SandboxBar";
import ProtectedRoute from "./components/ProtectedRoute";
import { useSocket } from "./components/Contexts/SocketContext"; // Import the SocketProvider
import { ToastContainer, toast } from "react-toastify"; // Import ToastContainer
import "react-toastify/dist/ReactToastify.css"; // Import CSS for toast notifications

interface System {
  id: number;
  system_name: string;
  patient_name: string;
  status: string;
  battery_lvl: number;
  charge_status: boolean;
  claimedby: string | null;
  last_checked: number | null;
  note: string | null;
}

const App = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<string | null>(null);
  const [selectedUserName, setSelectedUserName] =
    useState<string>("Online Users");
  const auth = useAuth();
  const { socket } = useSocket(); // Get the socket instance here
  const handleUserSelect = (userId: string | null, username: string) => {
    setSelectedUser(userId);
    setSelectedUserName(username);
  };

  // Whenever the signed-in user changes (login, logout, or session swap),
  // reset the "viewing another user's systems" picker. Without this, if
  // user A picked user B in the dropdown and then logged out, user C
  // logging in next would inherit "viewing user B's systems" — which
  // would either show empty (if C isn't allowed) or show B's data again.
  useEffect(() => {
    setSelectedUser(null);
    setSelectedUserName("Online Users");
  }, [auth.user?.sub]);

  // Easter egg: personalized greeting once per session
  useEffect(() => {
    if (!auth.isAuthenticated || !auth.user) return;
    const username = auth.user.username?.toLowerCase() || "";
    const email = (auth.user as any).email?.toLowerCase?.() || "";
    const greetings: Record<string, string> = {
      "sarkozrosh@unitedneuro.com":
        "🎉 Welcome back, Sar! Big brother says hi 👋. Try not to break anything today.",
      "kdevsam@gmail.com":
        "🛠️ Welcome back, boss. All systems are yours to break.",
    };
    const message = greetings[username] || greetings[email];
    if (!message) return;
    if (sessionStorage.getItem("greet_shown") === "1") return;
    sessionStorage.setItem("greet_shown", "1");
    toast.info(message, {
      position: "top-center",
      autoClose: 6000,
      closeOnClick: true,
      style: { fontWeight: 600 },
    });
  }, [auth.isAuthenticated, auth.user]);

  const toggleMenu = () => {
    setIsOpen(!isOpen);
  };

  const moveSystemToDashboard = (id: number) => {
    if (socket) {
      socket.emit("claimSystem", id);
    }
    // If the operator was browsing someone else's claimed systems, snap
    // back to their own view so the system they just claimed shows up
    // immediately. Otherwise they'd stay on the other user's dashboard
    // and have to manually switch back to see what they did.
    if (selectedUser && selectedUser !== auth?.user?.sub) {
      setSelectedUser(null);
      setSelectedUserName("Online Users");
    }
  };

  const unclaimSystem = (id: number) => {
    if (socket) {
      socket.emit("unclaimSystem", id);
    }
  };

  // Platform-owner shell — admin.epicarealert.com. Bypasses the
  // org-scoped Dashboard, NavBar, SystemsProvider, etc. since those are
  // all per-org concerns. Reuses the existing Login page (so MFA, refresh
  // tokens, etc. all work as-is), then routes to SuperAdminDashboard for
  // authenticated superadmins. Non-superadmin users who somehow log in
  // here get bounced back to login.
  if (isSuperAdminSubdomain()) {
    return (
      <>
        <ToastContainer position="top-right" autoClose={4000} pauseOnHover={false} pauseOnFocusLoss={false} style={{ width: "300px", right: "16px", top: "16px" }} />
        <Routes>
          <Route path="/" element={<Login />} />
          <Route element={<ProtectedRoute />}>
            <Route
              path="/dashboard"
              element={
                auth?.user?.role === "superadmin" ? (
                  <SuperAdminDashboard />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <>
      <SystemsProvider>
      <BillingProvider>
        {" "}
        {/* Wrap the app with SystemsProvider */}
        <ToastContainer position="top-right" autoClose={4000} pauseOnHover={false} pauseOnFocusLoss={false} style={{ width: "300px", right: "16px", top: "16px" }} />
        <OnlineUsersProvider>
          {" "}
          {/* Wrap the app in the SocketProvider */}
          <NavBar
            toggleMenu={toggleMenu}
            selectedUserName={selectedUserName}
            onUserSelect={handleUserSelect}
            isViewingOtherUser={!!selectedUser && selectedUser !== auth?.user?.sub}
          />
          <BillingBanner />
          {isSandbox() && <SandboxBar />}
          <Routes>
            <Route path="/" element={isSandbox() ? <SandboxGate /> : <Login />} />

            {/* Only authenticated users can access these routes */}
            <Route element={<ProtectedRoute />}>
              <Route
                path="/dashboard"
                element={
                  <Dashboard
                    isOpen={isOpen}
                    setIsOpen={setIsOpen}
                    unclaimSystem={unclaimSystem}
                    moveSystemToDashboard={moveSystemToDashboard}
                    selectedUser={selectedUser}
                    selectedUserName={selectedUserName}
                  />
                }
              />

              {/* Add the CheckLog route */}
              <Route path="/checklogs" element={<CheckLog />} />

              {/* Conditionally show the admin panel based on user role */}
              {isAdminLike(auth?.user?.role) && (
                <>
                  <Route path="/admin" element={<AdminPanel />} />
                  <Route path="/auditlogs" element={<AuditLog />} />
                </>
              )}
            </Route>

            {/* Catch-all route */}
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </OnlineUsersProvider>
      </BillingProvider>
      </SystemsProvider>
    </>
  );
};

export default App;
